var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { isNoShowNote } from 'constants/note-schemas';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import uniq from 'lodash/uniq';
import { useMembersById } from './useMembersById';
import { useMemberAppointments, useMyAppointments, } from './useMyAppointments';
import { useNotesForMember } from './useNotesForMembers';
import { useProvidersForTable } from './useProvidersForTable';
import { getNotesIndexedByAppointmentId, useNotesForUser, } from './useNotesForUser';
var getOwnersIndexes = function (appointments) {
    return appointments.reduce(function (out, a) {
        var _a;
        if (((_a = a.member) === null || _a === void 0 ? void 0 : _a.id) && !out.membersIndex[a.member.id]) {
            out.membersIndex[a.member.id] = a.member;
        }
        if (a.care_provider && !out.providersIndex[a.care_provider.userId]) {
            out.providersIndex[a.care_provider.userId] = a.care_provider;
        }
        return out;
    }, { membersIndex: {}, providersIndex: {} });
};
var generateRiskConcerns = function (note, date, appointments, notesIndexedByAppointmentId, memberId, providerId) {
    var _a, _b, _c, _d;
    if (!date || !appointments || !notesIndexedByAppointmentId || !providerId || !memberId) {
        return {}; // short circuit if anything hasn't loaded
    }
    if (((_a = note === null || note === void 0 ? void 0 : note.assessment) === null || _a === void 0 ? void 0 : _a.riskConcerns) && (note === null || note === void 0 ? void 0 : note.signedAt) && !isNoShowNote(note)) {
        return __assign(__assign({}, note.assessment.riskConcerns), { date: (_b = note.signedAt) !== null && _b !== void 0 ? _b : note.updatedAt }); // if note has saved risk concerns, always use that
    }
    else {
        var sortedSignedAppointments = (_c = appointments === null || appointments === void 0 ? void 0 : appointments.filter(function (appt) {
            var _a, _b, _c;
            return DateTime.fromISO(appt.start_at) < DateTime.fromISO(date) && // appts before date
                ((_a = notesIndexedByAppointmentId[appt.id]) === null || _a === void 0 ? void 0 : _a.signedAt) && // appts that are signed
                !isNoShowNote(notesIndexedByAppointmentId[appt.id]) && // skip no-show notes
                ((_b = appt.care_provider) === null || _b === void 0 ? void 0 : _b.id) === providerId && // appts associated with the same provider
                ((_c = appt.member) === null || _c === void 0 ? void 0 : _c.id) === memberId;
        } // appt associated with same member
        )) === null || _c === void 0 ? void 0 : _c.sort(function (a, b) {
            var dta = DateTime.fromISO(a.start_at);
            var dtb = DateTime.fromISO(b.start_at);
            if (dta > dtb)
                return -1;
            if (dta < dtb)
                return 1;
            return 0;
        });
        return (sortedSignedAppointments === null || sortedSignedAppointments === void 0 ? void 0 : sortedSignedAppointments.length) &&
            ((_d = notesIndexedByAppointmentId[sortedSignedAppointments[0].id].assessment) === null || _d === void 0 ? void 0 : _d.riskConcerns)
            ? __assign(__assign({}, notesIndexedByAppointmentId[sortedSignedAppointments[0].id].assessment.riskConcerns), { date: sortedSignedAppointments[0].start_at }) : {};
    }
};
var generateNotesWithPlaceholders = function (appointments, notesIndexedByAppointmentId, unassociatedNotes, membersIndex, providersIndex) { return __spreadArray(__spreadArray([], ((appointments === null || appointments === void 0 ? void 0 : appointments.map(function (ra) {
    var _a, _b, _c;
    return {
        appointment: ra,
        note: notesIndexedByAppointmentId && notesIndexedByAppointmentId[ra.id],
        date: ra.start_at,
        member: ra.member,
        provider: ra.care_provider,
        riskConcerns: generateRiskConcerns(notesIndexedByAppointmentId && notesIndexedByAppointmentId[ra.id], ra.start_at, appointments, notesIndexedByAppointmentId, (_a = ra.member) === null || _a === void 0 ? void 0 : _a.id, (_b = ra.care_provider) === null || _b === void 0 ? void 0 : _b.id),
        overdue: (!notesIndexedByAppointmentId || !((_c = notesIndexedByAppointmentId[ra.id]) === null || _c === void 0 ? void 0 : _c.signedAt)) &&
            DateTime.fromISO(ra.start_at).plus({ days: 3 }) < DateTime.local(), // mark overdue if not signed and 3 days from when appointment was
    };
})) || []), true), (unassociatedNotes.map(function (note) {
    var _a;
    return {
        note: note,
        date: note.sessionInfo.start,
        member: membersIndex[note.rotomMemberId || ''] || {
            last_name: note.rotomMemberId,
            first_name: note.rotomMemberId,
            id: note.rotomMemberId,
        },
        provider: providersIndex[note.ownerId || ''],
        riskConcerns: generateRiskConcerns(note, note.sessionInfo.start, appointments, notesIndexedByAppointmentId, note.rotomMemberId, note.ownerId),
        overdue: !note.signedAt &&
            DateTime.fromISO((_a = note.createdAt) !== null && _a !== void 0 ? _a : '').plus({ days: 3 }) < DateTime.local(), // mark overdue if not signed and is 3 days from when first draft was saved
    };
}) || []), true); };
/**
 * A shared hook that manages getting both appointments from rotom, and
 * notes from the ehr.  It also offers an indexed collection of notes
 * that helps to connect the two datasets.
 */
export var useAppointmentsAndNotes = function (disabled) {
    var _a, _b, _c;
    var appointments = useMyAppointments(disabled);
    var userId = (_a = appointments.data) === null || _a === void 0 ? void 0 : _a.userId;
    var notes = useNotesForUser(userId, disabled);
    var unassociatedNotes = useMemo(function () { var _a; return ((_a = notes === null || notes === void 0 ? void 0 : notes.data) === null || _a === void 0 ? void 0 : _a.filter(function (n) { return n.rotomAppointmentId == null; })) || []; }, [notes.data]);
    var _d = useMemo(function () {
        var _a;
        return ((_a = appointments.data) === null || _a === void 0 ? void 0 : _a.appointments)
            ? getOwnersIndexes(appointments.data.appointments)
            : { membersIndex: {}, providersIndex: {} };
    }, [appointments.data]), appointmentsMembersIndex = _d.membersIndex, providersIndex = _d.providersIndex;
    var notesIndexedByAppointmentId = useMemo(function () { return getNotesIndexedByAppointmentId(notes.data); }, [notes.data]);
    var membersRequiringFetch = useMemo(function () {
        return unassociatedNotes
            .filter(function (note) { return note.rotomMemberId && !appointmentsMembersIndex[note.rotomMemberId]; })
            .map(function (note) { var _a; return (_a = note.rotomMemberId) !== null && _a !== void 0 ? _a : ''; });
    }, [unassociatedNotes, appointmentsMembersIndex]);
    var membersById = useMembersById(uniq(membersRequiringFetch));
    var membersIndex = useMemo(function () {
        var _a;
        return (__assign(__assign({}, appointmentsMembersIndex), (_a = membersById.data) === null || _a === void 0 ? void 0 : _a.reduce(function (out, m) {
            var _a;
            return (__assign(__assign({}, out), (_a = {}, _a[m.id] = m, _a)));
        }, {})));
    }, [appointmentsMembersIndex, membersById === null || membersById === void 0 ? void 0 : membersById.data]);
    // Only consider appointments that are active (i.e. not cancelled) OR that have an associated note
    var relevantAppointments = (_c = (_b = appointments.data) === null || _b === void 0 ? void 0 : _b.appointments) === null || _c === void 0 ? void 0 : _c.filter(function (a) {
        return a.status !== 'Cancelled' || !!notesIndexedByAppointmentId[a.id];
    });
    var notesWithPlaceholders = generateNotesWithPlaceholders(relevantAppointments, notesIndexedByAppointmentId, unassociatedNotes, membersIndex, providersIndex);
    return __assign(__assign({}, appointments.data), { appointments: relevantAppointments, notesWithPlaceholders: notesWithPlaceholders, notes: notes.data, 
        /** An object with appointment id as a lookup, returning the note for that appointment if any. */
        notesIndexedByAppointmentId: notesIndexedByAppointmentId, 
        /** combined isLoading from appointments and notes */
        isLoading: appointments.isLoading || notes.isLoading, 
        /** combined isError from appointments and notes */
        isError: appointments.isError || notes.isError, membersById: membersById });
};
/**
 * A shared hook that manages getting both appointments from rotom, and
 * notes from the ehr.  It also offers an indexed collection of notes
 * that helps to connect the two datasets.
 */
export var useMemberAppointmentsAndNotes = function (member) {
    var _a;
    var _b, _c;
    var appointments = useMemberAppointments({ userId: member === null || member === void 0 ? void 0 : member.user.id });
    var notes = useNotesForMember(member === null || member === void 0 ? void 0 : member.id);
    var unassociatedNotes = useMemo(function () { var _a; return ((_a = notes === null || notes === void 0 ? void 0 : notes.data) === null || _a === void 0 ? void 0 : _a.filter(function (n) { return n.rotomAppointmentId == null; })) || []; }, [notes.data]);
    var notesIndexedByAppointmentId = useMemo(function () { return getNotesIndexedByAppointmentId(notes.data); }, [notes.data]);
    // Only consider appointments that are active (i.e. not cancelled) OR that have an associated note
    var relevantAppointments = (_c = (_b = appointments.data) === null || _b === void 0 ? void 0 : _b.appointments) === null || _c === void 0 ? void 0 : _c.filter(function (a) {
        return a.status !== 'Cancelled' || !!notesIndexedByAppointmentId[a.id];
    });
    var appointmentsProviderIndex = useMemo(function () {
        var _a;
        return ((_a = appointments.data) === null || _a === void 0 ? void 0 : _a.appointments)
            ? getOwnersIndexes(appointments.data.appointments)
            : { membersIndex: {}, providersIndex: {} };
    }, [appointments.data]).providersIndex;
    var providersRequiringFetch = useMemo(function () {
        return unassociatedNotes
            .filter(function (note) { return note.ownerId && !appointmentsProviderIndex[note.ownerId]; })
            .map(function (note) { var _a; return (_a = note.ownerId) !== null && _a !== void 0 ? _a : ''; });
    }, [unassociatedNotes, appointmentsProviderIndex]);
    var providersById = useProvidersForTable(uniq(providersRequiringFetch));
    var providersIndex = useMemo(function () {
        var _a;
        return (__assign(__assign({}, appointmentsProviderIndex), (_a = providersById.data) === null || _a === void 0 ? void 0 : _a.reduce(function (out, p) {
            var _a;
            return (__assign(__assign({}, out), (_a = {}, _a[p.id] = __assign({ userId: p.id, first_name: p.first_name, last_name: p.last_name, userRoles: p.roles }, p.care_provider), _a)));
        }, {})));
    }, [appointmentsProviderIndex, providersById === null || providersById === void 0 ? void 0 : providersById.data]);
    var notesWithPlaceholders = generateNotesWithPlaceholders(relevantAppointments, notesIndexedByAppointmentId, unassociatedNotes, member ? (_a = {}, _a[member.id] = member, _a) : {}, providersIndex);
    return __assign(__assign({}, appointments.data), { notesWithPlaceholders: notesWithPlaceholders, appointments: relevantAppointments, notes: notes.data, 
        /** An object with appointment id as a lookup, returning the note for that appointment if any. */
        notesIndexedByAppointmentId: notesIndexedByAppointmentId, 
        /** combined isLoading from appointments and notes */
        isLoading: appointments.isLoading || notes.isLoading, 
        /** combined isError from appointments and notes */
        isError: appointments.isError || notes.isError, providersById: providersById });
};
